import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { type User } from '../../entity/api/user';
import { getQueryClient } from '../../sdk/request';
import { resetPostHogUser } from '../posthog';
import { createBasicStoreState } from './basic-store';
import { useStore as useUIKeyValueStore } from './ui-key-value';

let clearStores: (() => void) | undefined;

export function setClearStores(fn: typeof clearStores): void {
  clearStores = fn;
}

type TModel = User;

type TokenGetter = ((bypassRaw?: boolean) => Promise<string>) | undefined;
type ProviderSignOut = (() => Promise<void>) | undefined;

type StoreActions = {
  // Auth methods (KEEP IN ZUSTAND - not replaceable by TanStack Query)
  setAuthOnStore: (tokenGetter: TokenGetter, providerSignOut: ProviderSignOut) => void;
  getAuthToken: (bypassRaw?: boolean) => Promise<string>;
  signOut: () => Promise<void>;
};

type FullStoreState = ReturnType<typeof createBasicStoreState<TModel>> &
  StoreActions & {
    authUserId?: string;
    authUserTokenGetter?: TokenGetter;
    authProviderSignOut?: ProviderSignOut;
  };

export const useStore = create<FullStoreState>()(
  devtools(
    function (set, get) {
      const store: FullStoreState = {
        // Include basic store methods for backward compatibility
        // These allow other stores (organization-users, etc.) and components to work
        ...createBasicStoreState<TModel>(set, get),

        // logged user will be here
        authUserId: undefined,
        authUserTokenGetter: undefined,
        authProviderSignOut: undefined,

        /////////////////////////////////
        // Auth methods (KEEP IN ZUSTAND)

        setAuthOnStore: function (tokenGetter, providerSignOut) {
          const { authUserTokenGetter, authProviderSignOut } = get();
          if (authUserTokenGetter === tokenGetter && authProviderSignOut === providerSignOut) {
            return;
          }

          set({
            authUserTokenGetter: tokenGetter,
            authProviderSignOut: providerSignOut,
          });
        },

        getAuthToken: function (bypassRaw) {
          const getter = get().authUserTokenGetter;
          if (getter == null) {
            return Promise.resolve('');
          }

          return getter(bypassRaw);
        },

        signOut: async function () {
          const { authProviderSignOut } = get();
          if (authProviderSignOut != null) {
            await authProviderSignOut();
          }

          resetPostHogUser();

          set({
            authUserId: undefined,
            authUserTokenGetter: undefined,
            authProviderSignOut: undefined,
          });

          // Clear UI state
          useUIKeyValueStore.getState().resetToStore();

          // Clear TanStack Query cache on sign out
          const queryClient = getQueryClient();
          queryClient.clear();

          // Clear remaining Zustand stores (registered from storage/index to avoid circular import)
          if (clearStores != null) {
            clearStores();
          }
          // NOTE: We don't need to redirect because if storage is empty, the router will redirect to the login page
        },
      };

      return store;
    },
    { name: 'blym', store: 'users' }
  )
);
